import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Mail, Lock, LogIn, Eye, EyeOff, User } from 'lucide-react'
import { Input } from '@/components/ui_custom/input'
import { Button } from '@/components/ui_custom/button'
import { Logo } from '@/components/common/logo'
import { useAuthStore } from '@/store/auth'
import { registerUserSchema, RegisterUserSchema } from '@/entities/user'
import { Form, FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { api } from '@/lib/axios'
import { useLoader as useLoaderPage } from '@/contexts/LoaderPageContext'
import { useLoader } from '@/contexts/LoaderContext'

// variables
const loc = '@/pages/Register'

export default function Register() {
  const { isAuthenticated } = useAuthStore()
  const { toast } = useToast()
  const { setIsLoading } = useLoaderPage()
  const { showLoader, hideLoader, isLoading } = useLoader()
  const location = useLocation()
  const [isShowingPassword, setIsShowingPassword] = useState(false)
  const [isShowingPasswordConfirm, setIsShowingPasswordConfirm] = useState(false)

  const form = useForm<RegisterUserSchema>({
    resolver: zodResolver(registerUserSchema),
    defaultValues: {
      email: location.state?.email || ''
    }
  })

  const formValues = form.watch()

  const handleSubmit = async () => {
    try {
      showLoader()

      const isValidForm = await form.trigger()
      if (!isValidForm) return
      
      const body = { ...formValues, action: 'register' }
      const response = await api.post(`/auth`, body)

      if (response.data?.statusCode === HTTP_STATUS_CODE.OK) {
        toast({ variant: 'success', title: 'Sucesso!', description: 'Cadastro realizado com sucesso.' })
        setTimeout(() => {
          window.location.href = '/login'
        }, 2000)
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.details || 'Não foi possível realizar o cadastro.' })
    } catch (err) {
      console.error(`Unhandled rejection at ${loc}.handleSubmit function. Details: ${err}`)
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível realizar o cadastro.' })
    } finally {
      setTimeout(() => hideLoader(), 1000)
    }
  }

  useEffect(() => {
    if (isAuthenticated) window.location.href = '/chat'
  }, [isAuthenticated])

  useEffect(() => {
    if (!location.state?.email) {
      window.location.href = '/'
    }
  }, [])

  return (
    <div className="app-shell min-h-screen min-w-full flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-5 bg-card p-8 rounded-3xl shadow-elevated border border-border/50">
        <div className="flex justify-center items-center cursor-pointer" onClick={() => (window.location.href = '/')}>
          <Logo size="md" invert={true} />
        </div>

        <h1 className="text-center text-xl font-bold text-foreground">Crie sua conta</h1>

        <Form {...form}>
          <form
            className="w-full disabled:cursor-not-allowed flex flex-col gap-6"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSubmit()
              }
            }}
          >
            <div className="space-y-4">
              <FormField
                control={form.control}
                name={'email'}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormControl>
                      <Input {...field} id="email" name="email" type="email" autoComplete="email" required label="E-mail" labelClass="text-foreground font-medium" placeholder="Digite seu e-mail" leftIcon={<Mail className="h-5 w-5" />} disabled={!!location.state?.email} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'name'}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormControl>
                      <Input {...field} id="name" name="name" type="text" autoComplete="name" required label="Nome" labelClass="text-foreground font-medium" placeholder="Digite seu nome" leftIcon={<User className="h-5 w-5" />} />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'password'}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormControl>
                      <Input
                        {...field}
                        id="password"
                        name="password"
                        type={isShowingPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        label="Senha"
                        labelClass="text-foreground font-medium"
                        placeholder="Digite sua senha"
                        leftIcon={<Lock className="h-5 w-5" />}
                        rightIcon={isShowingPassword ? <EyeOff className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingPassword(prev => !prev)} /> : <Eye className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingPassword(prev => !prev)} />}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'passwordConfirm'}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormControl>
                      <Input
                        {...field}
                        id="passwordConfirm"
                        name="passwordConfirm"
                        type={isShowingPasswordConfirm ? 'text' : 'password'}
                        autoComplete="password-confirm"
                        required
                        label="Confirme sua senha"
                        labelClass="text-foreground font-medium"
                        placeholder="Digite sua senha novamente"
                        leftIcon={<Lock className="h-5 w-5" />}
                        rightIcon={isShowingPasswordConfirm ? <EyeOff className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingPasswordConfirm(prev => !prev)} /> : <Eye className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingPasswordConfirm(prev => !prev)} />}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="button" variant="primary" className="w-full" isLoading={isLoading} disabled={isLoading} onClick={handleSubmit} leftIcon={<LogIn className="h-5 w-5" />}>
              Cadastrar
            </Button>
          </form>
        </Form>
      </div>
    </div>
  )
}
